/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Share2, ImageIcon, Upload, Clock, CheckCircle2, XCircle, Plus, Calendar, FileText, Target
} from 'lucide-react';
import { Button } from '../../components/ui/button';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-700', label: 'Pending' },
  approved: { icon: CheckCircle2, color: 'bg-green-100 text-green-700', label: 'Approved' },
  declined: { icon: XCircle, color: 'bg-red-100 text-red-700', label: 'Declined' },
  in_progress: { icon: Clock, color: 'bg-blue-100 text-blue-700', label: 'In Progress' },
};

const PortalSocialRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [formData, setFormData] = useState({
    business_name: '',
    campaign_topic: '',
    event_details: '',
    logo_url: '',
    images: [],
    video_url: '',
    desired_date: '',
    cta: '',
    preferred_message: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API}/portal/social-requests`, { withCredentials: true });
      setRequests(res.data);
    } catch (err) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/portal/profile/gallery`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (type === 'logo') {
        setFormData(prev => ({ ...prev, logo_url: res.data.image_url }));
      } else {
        setFormData(prev => ({ ...prev, images: [...prev.images, res.data.image_url] }));
      }
      toast.success(`${type === 'logo' ? 'Logo' : 'Image'} uploaded!`);
    } catch (err) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.business_name.trim() || !formData.campaign_topic.trim() || !formData.desired_date) {
      toast.error('Please fill in business name, campaign topic, and desired date');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/portal/social-requests`, formData, { withCredentials: true });
      toast.success('Social media request submitted!');
      setFormData({
        business_name: '', campaign_topic: '', event_details: '', logo_url: '',
        images: [], video_url: '', desired_date: '', cta: '', preferred_message: '', notes: '',
      });
      setView('list');
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="portal-social-requests">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-pink-500 rounded-sm flex items-center justify-center">
            <Share2 className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#002249]">Social Media Requests</h1>
        </div>
        <p className="text-slate-600 mt-2">
          Request social media promotion through the chamber's channels. Provide your campaign details and assets.
        </p>
      </motion.div>

      {/* View Toggle */}
      <div className="flex space-x-2 mb-6">
        <button
          onClick={() => setView('list')}
          className={`px-4 py-2 rounded-sm text-sm font-medium transition-all ${view === 'list' ? 'bg-[#002249] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
          data-testid="view-social-list"
        >
          My Requests ({requests.length})
        </button>
        <button
          onClick={() => setView('new')}
          className={`px-4 py-2 rounded-sm text-sm font-medium transition-all flex items-center ${view === 'new' ? 'bg-[#002249] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
          data-testid="new-social-request-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> New Request
        </button>
      </div>

      {/* New Request Form */}
      {view === 'new' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="p-5 border-b border-slate-200">
              <h2 className="text-lg font-bold text-[#002249]">Submit Social Media Request</h2>
              <p className="text-sm text-slate-500 mt-1">Provide all the details for your social media campaign. Your request will go directly to the admin dashboard.</p>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-5" data-testid="social-request-form">
              {/* Business Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Business Name <span className="text-[#E63946]">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.business_name}
                    onChange={e => setFormData({ ...formData, business_name: e.target.value })}
                    placeholder="Your business name"
                    className="w-full border border-slate-200 rounded-sm p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]"
                    data-testid="social-business-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Campaign Topic <span className="text-[#E63946]">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.campaign_topic}
                    onChange={e => setFormData({ ...formData, campaign_topic: e.target.value })}
                    placeholder="e.g., Grand Opening, Sale, New Service"
                    className="w-full border border-slate-200 rounded-sm p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]"
                    data-testid="social-campaign-topic"
                  />
                </div>
              </div>

              {/* Campaign Details */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Event / Campaign Details</label>
                <textarea
                  value={formData.event_details}
                  onChange={e => setFormData({ ...formData, event_details: e.target.value })}
                  placeholder="Describe the event or campaign in detail..."
                  className="w-full border border-slate-200 rounded-sm p-3 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]"
                  data-testid="social-event-details"
                />
              </div>

              {/* Preferred Message */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Preferred Message / Caption</label>
                <textarea
                  value={formData.preferred_message}
                  onChange={e => setFormData({ ...formData, preferred_message: e.target.value })}
                  placeholder="Write the message or caption you'd like used in the social media post..."
                  className="w-full border border-slate-200 rounded-sm p-3 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]"
                  data-testid="social-preferred-message"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* CTA */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Call-to-Action (CTA)</label>
                  <input
                    type="text"
                    value={formData.cta}
                    onChange={e => setFormData({ ...formData, cta: e.target.value })}
                    placeholder="e.g., Visit our website, Call now, Book today"
                    className="w-full border border-slate-200 rounded-sm p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]"
                    data-testid="social-cta"
                  />
                </div>
                {/* Deadline */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Desired Post Date <span className="text-[#E63946]">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.desired_date}
                    onChange={e => setFormData({ ...formData, desired_date: e.target.value })}
                    className="w-full border border-slate-200 rounded-sm p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]"
                    data-testid="social-desired-date"
                  />
                </div>
              </div>

              {/* Logo Upload */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Logo Upload</label>
                <div className="flex items-center space-x-3">
                  <label className="flex items-center px-4 py-2 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50 text-sm" data-testid="social-logo-upload">
                    <Upload className="w-4 h-4 mr-2 text-slate-500" />
                    {uploading ? 'Uploading...' : 'Upload Logo'}
                    <input type="file" accept="image/*" onChange={e => handleFileUpload(e, 'logo')} className="hidden" />
                  </label>
                  {formData.logo_url && (
                    <Img variant="business" src={resolveImg(formData.logo_url)} alt="Logo" className="w-12 h-12 object-contain border rounded-sm" />
                  )}
                </div>
              </div>

              {/* Image Uploads */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Campaign Images</label>
                <label className="inline-flex items-center px-4 py-2 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50 text-sm mb-2" data-testid="social-images-upload">
                  <ImageIcon className="w-4 h-4 mr-2 text-slate-500" />
                  {uploading ? 'Uploading...' : 'Upload Images'}
                  <input type="file" accept="image/*" onChange={e => handleFileUpload(e, 'image')} className="hidden" />
                </label>
                {formData.images.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.images.map((img, i) => (
                      <div key={i} className="relative">
                        <Img variant="generic"
                          src={resolveImg(img)}
                          alt={`Campaign ${i + 1}`}
                          className="w-20 h-20 object-cover rounded-sm border"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(i)}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center"
                          data-testid={`remove-image-${i}`}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Video URL */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Video URL (optional)</label>
                <input
                  type="url"
                  value={formData.video_url}
                  onChange={e => setFormData({ ...formData, video_url: e.target.value })}
                  placeholder="https://youtube.com/watch?v=..."
                  className="w-full border border-slate-200 rounded-sm p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]"
                  data-testid="social-video-url"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Additional Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any other details or special instructions..."
                  className="w-full border border-slate-200 rounded-sm p-3 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]"
                  data-testid="social-notes"
                />
              </div>

              <div className="pt-4 flex space-x-3">
                <Button type="button" variant="outline" className="rounded-sm" onClick={() => setView('list')} data-testid="cancel-social-btn">
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="bg-[#002249] hover:bg-[#001630] rounded-sm" data-testid="submit-social-btn">
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </div>
            </form>
          </div>
        </motion.div>
      )}

      {/* Requests List */}
      {view === 'list' && (
        <div className="space-y-3" data-testid="social-requests-list">
          {requests.length > 0 ? requests.map((req, index) => {
            const status = statusConfig[req.status] || statusConfig.pending;
            return (
              <motion.div
                key={req.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.03 * index }}
                className="bg-white border border-slate-200 rounded-sm p-5"
                data-testid={`social-request-item-${index}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-[#002249]">{req.campaign_topic}</h3>
                    <p className="text-sm text-slate-500">{req.business_name}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-sm flex items-center ${status.color}`}>
                    <status.icon className="w-3.5 h-3.5 mr-1" />
                    {status.label}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-slate-600">
                  <div className="flex items-center">
                    <Calendar className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
                    {req.desired_date ? new Date(req.desired_date + 'T00:00:00').toLocaleDateString() : 'No date'}
                  </div>
                  {req.cta && (
                    <div className="flex items-center">
                      <Target className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
                      {req.cta}
                    </div>
                  )}
                  {req.images?.length > 0 && (
                    <div className="flex items-center">
                      <ImageIcon className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
                      {req.images.length} image(s)
                    </div>
                  )}
                  <div className="flex items-center">
                    <FileText className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
                    Submitted {new Date(req.created_at).toLocaleDateString()}
                  </div>
                </div>
                {req.preferred_message && (
                  <p className="mt-3 text-sm text-slate-600 bg-slate-50 p-3 rounded-sm">
                    <span className="font-medium">Message:</span> {req.preferred_message}
                  </p>
                )}
              </motion.div>
            );
          }) : (
            <div className="text-center py-16 text-slate-500">
              <Share2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium">No social media requests yet</p>
              <p className="text-sm mt-1">Request promotion for your business through the chamber's social channels.</p>
              <Button className="mt-4 bg-[#002249] hover:bg-[#001630] rounded-sm" onClick={() => setView('new')} data-testid="create-first-social-btn">
                <Plus className="w-4 h-4 mr-2" /> Create Your First Request
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PortalSocialRequests;
